import {
    FormLayout,
    Form,
    TextField,
    Button,
    Select,
    LegacyStack,
    Toast,
    Frame,
    Checkbox,
    Text,
} from "@shopify/polaris";
import { useState, useCallback, useEffect } from 'react';
import Api from '../../helpers/api';
import {cloneDeep} from 'lodash';
import {CountrySelector, StateSelector} from "../Selectors";
import {filterShippingMethods, getFormError} from "../../helpers/utils";
import {useToast} from "@shopify/app-bridge-react";

const CustomerForm = props => {
    
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    const emptyCustomer = () => {
        return {
            'status': 'approved',  // Internal customers are just approved.
            'first_name': '',
            'last_name': '',
            'email': '',
            'shipping_method': null,
            'credit_limit': '',
            'discount': '',
            'phone': '',
            'notes': '',
            'is_on_hold': false,
            'account_number': '',
            'shopify_company_location_id': '',
            credit_enabled: false,
            credit_balance: 0,

            sync_hash: null,
        }
    };

    const existingCustomer = () => {
        return {
            ...props.customer,
            ...props.customer.additional_data,
            id: props.customer.id,
            status: props.customer.status,
            credit_enabled: props.customer.credit_enabled ?? false,
            credit_balance: props.customer.credit_balance ?? 0,
            sync_hash: props.customer.sync_hash,
        }
    }

    const [form, setForm] = useState((props.customer && props.updating)
        ? existingCustomer()
        : emptyCustomer() );

    const [errors, setErrors] = useState({});

    const handleInputChange = useCallback((value, id) => {
        const payload = {
            [id]: value,
        };

        setForm({
            ...form,
            ...payload,
        });
    }, [form]);

    const handleSubmit = () => {
        setLoading(true);
        setErrors({});

        Api.upsertCustomer(form)
            .then (({ data }) => {
                props.onCustomerCreated(data.data);
                setLoading(false);
            })
            .catch ( error => {
                if (error.response.status == 422) {
                    if (error.response.data.errors.shopify_error) {
                        toast.show(error.response.data.errors.shopify_error[0], {isError: true});
                    } else {
                        toast.show("There has been an error processing the form.", {isError: true});
                        setErrors(error.response.data.errors);
                    }
                } else {
                    toast.show("There has been an error processing the form.", {isError: true});
                }

                setLoading(false);

                if (error.response.status != 422) {
                    throw error;
                }
            });
    }

    const showError = name => getFormError(errors, name);

    return (
        <Frame>
            <Form onSubmit={handleSubmit}>
            <FormLayout>
                <FormLayout.Group>
                    <Checkbox
                        checked={form.credit_enabled}
                        onChange={handleInputChange}
                        label="Credit Enabled"
                        id="credit_enabled"
                        error={showError('credit_enabled')}
                        helpText={form.credit_expires_at ? `Credit expires at ${form.credit_expires_at}` : null}
                    />
                    <TextField
                        value={(form.credit_enabled ? form.credit_balance : 0) + ''}
                        onChange={handleInputChange}
                        disabled={!form.credit_enabled}
                        label="Credit Balance"
                        type="number"
                        id="credit_balance"
                        error={showError('credit_balance')}
                    />
                </FormLayout.Group>
                <FormLayout.Group>
                        <TextField value={form.credit_limit} onChange={handleInputChange} label="Credit Limit" type="text" id="credit_limit" error={showError('credit_limit')} />
                        <TextField value={form.discount} onChange={handleInputChange} label="Discount (%)" type="text" id="discount" error={showError('discount')} />
                </FormLayout.Group>

                <hr />
                <Text variant="headingSm" as="h3">Contact Information</Text>
                <FormLayout.Group>
                    <LegacyStack vertical>
                        <TextField value={form.email} onChange={handleInputChange} label="Email" type="email" id="email" error={showError('email')} />
                        <TextField label="Phone" value={form.phone} onChange={handleInputChange} type="tel" id="phone" error={showError('phone')} />
                    </LegacyStack>
                    <LegacyStack vertical>
                        <TextField label="First Name" value={form.first_name} onChange={handleInputChange} type="text" id="first_name" error={showError('first_name')} />
                        <TextField label="Last Name" value={form.last_name} onChange={handleInputChange} type="text" id="last_name" error={showError('last_name')} />
                    </LegacyStack>
                </FormLayout.Group>

                <hr />
                <FormLayout.Group>
                    <TextField label="Notes" value={form.notes} onChange={handleInputChange} type="text" id="notes" error={showError('notes')} multiline={4} />
                </FormLayout.Group>
                <Button loading={loading} submit  variant="primary">Submit</Button>
            </FormLayout>
            </Form>
        </Frame>
    );
}

export default CustomerForm;
